import React from "react";
import Modal from "../Modal";
import {CampaignRecipient} from "../../redux/campaignTypes";
import {createUseStyles} from "react-jss";
import {Colors} from "../../constants/colors";
import {ColumnDef, flexRender, getCoreRowModel, getPaginationRowModel, useReactTable,} from '@tanstack/react-table';
import Pagination from "../Pagination";

type Props = {
  recipients: CampaignRecipient[];
  notice?: string;
  visible: boolean;
  onCloseClick: () => void;
};

const CampaignRecipientsModal = ({ recipients, notice, visible, onCloseClick }: Props) => {
  const styles = useStyles();

  const columns: ColumnDef<CampaignRecipient>[] = [
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row) => `${row.givenName} ${row.familyName}`,
    },
    {
      id: 'identificationNumber',
      header: 'Identification number',
      accessorFn: (row) => row.identificationNumber,
    },
  ];

  const table = useReactTable({
    data: recipients,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
  });

  const totalItems = recipients.length;
  const pageSize = table.getState().pagination.pageSize;
  const shouldShowPagination = totalItems > pageSize;

  return (
    <Modal
      show={visible}
      title={`Recipients (${recipients.length})`}
      okButtonTitle="Close"
      hideCancelButton={true}
      onHide={onCloseClick}
      onOkClick={onCloseClick}
      onCancelClick={onCloseClick}
      size="lg"
    >
      <div className={styles.container}>
        {recipients.length === 0 ? (
          <p>No recipients found.</p>
        ) : (
          <>
            {notice && (
              <div className={styles.notice}>
                <p>
                  {notice}
                </p>
              </div>
            )}

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id} className={styles.headerRow}>
                    {headerGroup.headers.map(header => (
                      <th key={header.id} className={styles.headerCell}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </th>
                    ))}
                  </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className={styles.bodyRow}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className={styles.bodyCell}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
                </tbody>
              </table>
            </div>

            {shouldShowPagination && (
              <div className={styles.pagination}>
                <Pagination
                  pageIndex={table.getState().pagination.pageIndex + 1}
                  itemCount={totalItems}
                  pageSize={pageSize}
                  siblingCount={1}
                  onPageChange={(pageIndex) => table.setPageIndex(pageIndex - 1)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    color: Colors.white,
  },
  notice: {
    padding: '12px 16px',
    backgroundColor: Colors.darkGrayCard,
    borderRadius: '6px',
    border: `1px solid ${Colors.darkGrayBorder}`,
    fontSize: '13px',
    lineHeight: '1.4',
    '& p': {
      margin: 0,
      color: Colors.darkGray1,
    }
  },
  tableContainer: {
    overflow: 'auto',
    maxHeight: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '14px',
  },
  headerRow: {
    backgroundColor: 'transparent',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    backgroundColor: Colors.darkGrayCard,
    borderRadius: '4px',
    fontWeight: 'bold',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  bodyRow: {
    backgroundColor: Colors.darkGrayCard,
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  bodyCell: {
    padding: '2px 12px',
    textAlign: 'left',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center'
  }
});

export default CampaignRecipientsModal;
